import React from 'react';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { FORM_CONFIG, FieldKey } from './connection-template-form.config';
import { ConnectionTemplatesFormVM } from './connection-template-form.viewmodel';
import Text from '../../../global/text/text';
import { RichTextEditor } from '../../../global/rich-text-editor';
import { ConnectionTemplatesList } from './components/conections-list/conections-list';
import './connection-template.form.scss';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import OptionsButton, { IButtonOption } from '../../../global/options-button/options-button';
import { MORE_BUTTON_TEXT, cancelText, submitText } from '../../../../constants/common-labels.constants';
import { AUTO_REGISTRATION_ENABLED_TEXT, AUTO_SUBSCRIPTION_ENABLED_TEXT, BLUEPRINT_DELETE_TEMPLATE_TEXT } from './connection-template-form.texts';
import { FormFieldInput } from '../../../global/form/form-field-input/form-field-input';
import { EnablementStatus } from '../../../../constants/common.constants';
import ConnectionTemplateConnectionGroupList from './connection-template-connection-groups/connection-template-connection-groups-list';
import { ConnectionTemplateRoleConnections } from './connection-template-role-connections/connection-template-role-connections';
import ConnectionTemplatePodiumSection from './connection-template-podium/connection-template-podium';

export type ConnectionTemplateFormPresentationProps = {
    vm: ConnectionTemplatesFormVM;
};

export const ConnectionTemplateFormPresentation = observer(({ vm }: ConnectionTemplateFormPresentationProps): JSX.Element => {
    const intl = useIntl();

    const {
        name,
        description,
        guidelines,
        connectionVersion,
        autoRegistrationEnabled,
        autoSubscriptionEnabled,
    } = vm.getFormFields(FieldKey);

    const formOptions: IButtonOption[] = [
        vm.isEditMode && hasPermission(Permission.DELETE_CONNECTION_TEMPLATES) && {
            label: intl.formatMessage(BLUEPRINT_DELETE_TEMPLATE_TEXT),
            action: vm.deleteBluePrint,
            danger: true,
        },
    ].filter(Boolean);

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--connection-template'>
                    <div className='oe-form__body'>
                        <FormFieldInput.Text field={name} labelDescriptor={FORM_CONFIG[FieldKey.NAME].labelDescriptor} withError />

                        <FormFieldInput.Text field={description} labelDescriptor={FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor} withError />
                    </div>

                    <FormField label={<FormattedMessage {...FORM_CONFIG[FieldKey.GUIDELINES].labelDescriptor} />}>
                        <RichTextEditor
                            className='m-top-0px'
                            value={guidelines.value as string}
                            onChange={mobx.action((event, editor) => {
                                guidelines.setValue(editor.getData());
                            })}
                            onBlur={guidelines.setTouched}
                            validationError={
                                !guidelines.valid && guidelines.touched &&
                                <FormattedMessage
                                    values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.GUIDELINES].labelDescriptor) }}
                                    {...guidelines.validationMessage}
                                />
                            }
                        />
                    </FormField>

                    {connectionVersion.value && <FormFieldInput.Toggle
                        isSelected={autoRegistrationEnabled.value as boolean}
                        toggle={() => autoRegistrationEnabled.setValue(!autoRegistrationEnabled.value)}
                        text={<Text
                            {...AUTO_REGISTRATION_ENABLED_TEXT}
                            values={{
                                suffix: autoRegistrationEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            }}
                            className='replays-toggle-description'
                        />}
                        className='meeting-toggle'
                    />}

                    {connectionVersion.value && <FormFieldInput.Toggle
                        isSelected={autoSubscriptionEnabled.value as boolean}
                        toggle={() => autoSubscriptionEnabled.setValue(!autoSubscriptionEnabled.value)}
                        text={<Text
                            {...AUTO_SUBSCRIPTION_ENABLED_TEXT}
                            values={{
                                suffix: autoSubscriptionEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            }}
                            className='auto-subscription-toggle-description'
                        />}
                        className='meeting-toggle'
                    />}

                    <ConnectionTemplatesList vm={vm} />
                    { connectionVersion.value && <ConnectionTemplateConnectionGroupList vm={vm} /> }
                    <ConnectionTemplateRoleConnections vm={vm} />
                    <ConnectionTemplatePodiumSection vm={vm} />
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={vm.submitForm}
                        disabled={vm.isLoading}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                    {!!formOptions.length && <OptionsButton
                        className='options-button--right-margin-auto'
                        modifier={['fat', 'options']}
                        label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                        options={formOptions}
                        position='top'
                        align='start'
                    />}
                </div>
            }
        />
    );
});
